import { useState, useMemo } from "react";
import { stepLabel } from "@/lib/analyticsDB";
import type { AnalyticsSession } from "@/lib/analyticsDB";

const PAGE_SIZE = 10;

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({
  status,
}: {
  status: "complete" | "in-progress" | "dropped" | "form-submitted" | "trial-started";
}) {
  const styles = {
    complete: "bg-emerald-accent/10 text-emerald-accent border-emerald-accent/20",
    "in-progress": "bg-blue-500/10 text-blue-400 border-blue-500/20",
    dropped: "bg-red-500/10 text-red-400 border-red-500/20",
    "form-submitted": "bg-purple-500/10 text-purple-400 border-purple-500/20",
    "trial-started": "bg-teal-500/10 text-teal-400 border-teal-500/20",
  };
  const labels = {
    complete: "Completed",
    "in-progress": "In Progress",
    dropped: "Dropped",
    "form-submitted": "Demo Request",
    "trial-started": "Trial Started",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

interface SessionsTableProps {
  sessions: AnalyticsSession[];
}

export default function SessionsTable({ sessions }: SessionsTableProps) {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<"all" | "complete" | "dropped" | "form-submitted" | "trial-started">("all");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");

  const filtered = useMemo(() => {
    let data = [...sessions];
    if (filter === "complete") data = data.filter((s) => s.completed && !s.submittedForm && !s.trialStarted);
    if (filter === "dropped") data = data.filter((s) => !s.completed && !s.submittedForm && !s.trialStarted);
    if (filter === "form-submitted") data = data.filter((s) => s.submittedForm || s.demoBooked);
    if (filter === "trial-started") data = data.filter((s) => s.trialStarted);
    data.sort((a, b) => (sort === "newest" ? b.openedAt - a.openedAt : a.openedAt - b.openedAt));
    return data;
  }, [sessions, filter, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="glass-card p-4 md:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold text-white">Session History</h3>
          <p className="text-xs text-silver-400 mt-0.5">Track individual advisor sessions and outcomes</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value as typeof filter);
              setPage(0);
            }}
            className="bg-graphite-800/80 border border-graphite-700/60 rounded-md text-xs text-white px-2.5 py-1.5 focus:outline-none focus:border-emerald-accent/50"
          >
            <option value="all">All Sessions</option>
            <option value="complete">Completed</option>
            <option value="form-submitted">Demo Requests</option>
            <option value="trial-started">Trial Starts</option>
            <option value="dropped">Dropped</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            className="bg-graphite-800/80 border border-graphite-700/60 rounded-md text-xs text-white px-2.5 py-1.5 focus:outline-none focus:border-emerald-accent/50"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 md:-mx-5 md:px-5">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-graphite-700/50">
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider">Session</th>
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider">Page</th>
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider">Last Step</th>
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider">Status</th>
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider">Products</th>
              <th className="py-2.5 pr-4 text-[11px] font-semibold text-silver-400 uppercase tracking-wider text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-silver-400">
                  No sessions found.
                </td>
              </tr>
            )}
            {paginated.map((s) => {
              let status: "complete" | "in-progress" | "dropped" | "form-submitted" | "trial-started" = "dropped";
              if (s.trialStarted) status = "trial-started";
              else if (s.submittedForm || s.demoBooked) status = "form-submitted";
              else if (s.completed) status = "complete";
              else if (s.lastStep > 0) status = "in-progress";

              return (
                <tr key={s.id} className="border-b border-graphite-700/30 hover:bg-graphite-800/30 transition-colors">
                  <td className="py-2.5 pr-4">
                    <span className="text-xs font-mono text-silver-300">{s.id.slice(0, 18)}...</span>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-silver-300">{s.pagePath || "/"}</td>
                  <td className="py-2.5 pr-4">
                    <span className="text-xs text-silver-300">{stepLabel(s.lastStep)}</span>
                  </td>
                  <td className="py-2.5 pr-4">
                    <StatusBadge status={status} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {(s.productsRecommended || []).slice(0, 2).map((p) => (
                        <span
                          key={p}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-graphite-700/60 text-silver-300"
                        >
                          {p.replace("StackMap ", "")}
                        </span>
                      ))}
                      {(s.productsRecommended || []).length > 2 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-graphite-700/60 text-silver-400">
                          +{(s.productsRecommended || []).length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 pr-4 text-right text-xs text-silver-400 whitespace-nowrap">
                    {formatDate(s.openedAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-graphite-700/50">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="text-xs text-silver-400 hover:text-white disabled:text-silver-600 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-1" />
            Previous
          </button>
          <span className="text-xs text-silver-400">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="text-xs text-silver-400 hover:text-white disabled:text-silver-600 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            Next
            <i className="ri-arrow-right-line ml-1" />
          </button>
        </div>
      )}
    </div>
  );
}
